/**
 * CoreIcon — renders a single CoreUI icon from `/public/coreui-icons/svg/free/`.
 *
 * We use `mask-image` so the icon paints in `currentColor`, inheriting the
 * text color of whatever wraps it. That means:
 *   - On the off-black sidebar → icons paint WHITE (text is white)
 *   - On the cream canvas      → icons paint off-black
 *   - Hover over a link with accent color → icon follows
 *
 * Why this shape over <img>: <img> bakes the source color and can't be
 * re-colored. Inlining all 556 SVGs as React components would balloon the
 * bundle. mask-image is the cleanest tradeoff.
 *
 * The complete icon set ships in /public/coreui-icons so nothing is imported
 * at build time — each icon is one HTTP request cached by the browser.
 *
 * Name = the CoreUI "cil-*" filename without the .svg extension.
 *  e.g. `<CoreIcon name="inbox" />` → `/coreui-icons/svg/free/cil-inbox.svg`
 */

import { cn } from '@/lib/utils';

export function CoreIcon({
  name,
  className,
  size = 18,
  title,
}: {
  name: string;
  className?: string;
  size?: number | string;
  /** Optional accessible title; omit for decorative. */
  title?: string;
}) {
  const url = `url('/coreui-icons/svg/free/cil-${name}.svg')`;
  const style: React.CSSProperties = {
    width: size,
    height: size,
    backgroundColor: 'currentColor',
    WebkitMaskImage: url,
    maskImage: url,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
    display: 'inline-block',
    flexShrink: 0,
  };
  return (
    <span
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      className={cn(className)}
      style={style}
    />
  );
}
